import { VideoProject } from "@/services/bedrock";

import { isQaReviewStatus } from "./qaReview";
import { editorSubmitActionLabel, editorSubmitEvent, isEditorResubmit } from "./submitEvent";

export type EditorPageActionIntent =
  | { type: "status_event"; event: VideoProject.Event; userId?: number }
  | { type: "submit"; event: VideoProject.Event.SUBMIT_FOR_QA_REVIEW | VideoProject.Event.COMPLETE }
  | { type: "save" }
  | { type: "qa_review" }
  | { type: "view_qa" }
  | { type: "reopen" }
  | { type: "duplicate" }
  | { type: "archive" };

export interface EditorPageActionSpec {
  text: string;
  icon?: string;
  intent: EditorPageActionIntent;
}

export interface EditorPageActionMapping {
  primary?: EditorPageActionSpec;
  secondary?: EditorPageActionSpec;
  menu: EditorPageActionSpec[];
  coveredEvents: VideoProject.Event[];
}

const VIEW_QA_STUDIO_ACTION: EditorPageActionSpec = {
  text: "View QA studio",
  intent: { type: "view_qa" },
};

function withViewQaStudio(
  mapping: EditorPageActionMapping,
  options: { status: VideoProject.Status; archived?: boolean | null }
): EditorPageActionMapping {
  const continueQaReview =
    mapping.primary?.intent.type === "qa_review" &&
    options.status === VideoProject.Status.QA_REVIEW &&
    !options.archived;
  if (continueQaReview) return mapping;
  if (!mapping.secondary) {
    return { ...mapping, secondary: VIEW_QA_STUDIO_ACTION };
  }
  return { ...mapping, menu: [VIEW_QA_STUDIO_ACTION, ...mapping.menu] };
}

export function getEditorPageActions(options: {
  status: VideoProject.Status;
  archived?: boolean | null;
  requiresQa?: boolean | null;
  qaStatus?: VideoProject["qaStatus"];
  revisionCount?: number | null;
  canManageVideoProjectQueue?: boolean;
  hasPost?: boolean;
  currentUserId?: number;
}): EditorPageActionMapping {
  const { status, archived, requiresQa, qaStatus, revisionCount, canManageVideoProjectQueue, hasPost, currentUserId } =
    options;
  const coveredEvents: VideoProject.Event[] = [];
  let primary: EditorPageActionSpec | undefined;
  let secondary: EditorPageActionSpec | undefined;

  if (archived) {
    return withViewQaStudio(
      {
        primary: { text: "Duplicate", icon: "copy", intent: { type: "duplicate" } },
        menu: [],
        coveredEvents,
      },
      { status, archived }
    );
  }

  if (canManageVideoProjectQueue && isQaReviewStatus(status)) {
    primary = {
      text: status === VideoProject.Status.SUBMITTED_FOR_QA_REVIEW ? "Start QA review" : "Continue QA review",
      intent: { type: "qa_review" },
    };
    coveredEvents.push(VideoProject.Event.START_QA_REVIEW, VideoProject.Event.APPROVE_QA, VideoProject.Event.REJECT_QA);
  } else if (VideoProject.isCompletedStatus(status)) {
    secondary = { text: "Re-open", intent: { type: "reopen" } };
    coveredEvents.push(VideoProject.Event.REOPEN_EDITING);
  } else if (status === VideoProject.Status.SUBMITTED_FOR_EDITING) {
    primary = {
      text: "Start editing",
      intent: { type: "status_event", event: VideoProject.Event.START_EDITING, userId: currentUserId },
    };
    coveredEvents.push(VideoProject.Event.START_EDITING);
  } else if (status === VideoProject.Status.EDITING && hasPost) {
    const event = editorSubmitEvent(requiresQa);
    primary = {
      text: editorSubmitActionLabel(requiresQa, isEditorResubmit({ qaStatus, revisionCount })),
      intent: { type: "submit", event },
    };
    secondary = { text: "Save", intent: { type: "save" } };
    coveredEvents.push(event, VideoProject.Event.RESUBMIT);
    if (event === VideoProject.Event.SUBMIT_FOR_QA_REVIEW) {
      coveredEvents.push(VideoProject.Event.COMPLETE);
    } else {
      coveredEvents.push(VideoProject.Event.SUBMIT_FOR_QA_REVIEW);
    }
  }

  const menu: EditorPageActionSpec[] = [];

  menu.push({ text: "Duplicate", icon: "copy", intent: { type: "duplicate" } });
  menu.push({ text: "Archive", icon: "archive", intent: { type: "archive" } });

  return withViewQaStudio({ primary, secondary, menu, coveredEvents }, { status, archived });
}
